import { ArrowUpLeft } from "lucide-react";
import LocalizedLink from "@/components/LocalizedLink";
import { useLanguage } from "@/i18n/LanguageContext";

const EMERGENT_URL = "https://emergent.sh";

const C = {
  ar: {
    eyebrow: "ملحوظة تقنية · توثيق البناء",
    title: "تمّ بناء هذه المنصّة باستخدام",
    intro: "في مجتمع الذكاء الاصطناعي السوري، نُؤمن أن الذكاء الاصطناعي ليس مجرّد موضوع للتعريف أو النقاش، بل أداة عملية يمكن توظيفها لبناء حلول حقيقية وتطوير مشاريع رقمية بشكل أسرع وأكثر كفاءة.",
    h1: "لماذا استخدمنا",
    p1: "لأنّنا أردنا أن تعكس المنصّة نفسها الفكرة التي ننشرها ضمن SAS: استخدام أدوات الذكاء الاصطناعي بشكل عملي ومسؤول. اعتمدنا على منصّة Emergent ضمن عملية بناء المنصّة، حيث ساعدتنا في تسريع هيكلة الموقع، وتنظيم الصفحات، وتطوير التجربة بشكل أكثر مرونة، وهو ما يتوافق مع توجّهنا نحو توظيف التقنيات الحديثة لخدمة التعليم والمجتمع.",
    h2: "لماذا نُرشّحها؟",
    p2: "نرى أن Emergent تمثّل خيارًا قويًا للأفراد والفرق الذين يريدون تحويل الأفكار إلى منتجات رقمية حقيقية بسرعة أعلى ووضوح أكبر، خاصّةً عند العمل على مواقع ومنصّات تحتاج إلى تطوير متدرّج وتحسين مستمرّ.",
    closing: "اختيارنا لهذه المنصّة لم يكن مجرّد قرار تقني، بل جزء من رسالتنا في تبنّي الذكاء الاصطناعي كأداة بناء حقيقية يمكن أن تخدم المبادرات التعليمية والمشاريع المجتمعية في سوريا وخارجها.",
    visit: "زيارة منصّة",
    back: "العودة إلى صفحة المبادرة",
    note: "هذه الصفحة صفحة توثيق تقني من SAS؛ ليست إعلانًا ولا محتوى تسويقيًا، بل ملاحظة صريحة حول الأدوات المستخدمة في بناء المنصّة.",
  },
  en: {
    eyebrow: "Technical note · Build documentation",
    title: "This platform was built with",
    intro: "At the Syrian AI Society we believe AI is not just a subject to define or debate, but a practical tool that can be used to build real solutions and ship digital projects faster and more efficiently.",
    h1: "Why we used",
    p1: "Because we wanted the platform itself to reflect what we teach inside SAS: using AI practically and responsibly. We relied on Emergent during the build process — it helped us speed up the site structure, organise pages, and evolve the experience more flexibly. This aligns with our direction of employing modern technologies to serve education and the community.",
    h2: "Why we recommend it",
    p2: "We see Emergent as a strong option for individuals and teams who want to turn ideas into real digital products with more speed and more clarity — especially when working on sites and platforms that need gradual development and continuous improvement.",
    closing: "Choosing this platform wasn't just a technical decision; it is part of our mission to adopt AI as a real building tool that can serve educational initiatives and community projects in Syria and beyond.",
    visit: "Visit",
    back: "Back to the About page",
    note: "This is a technical documentation page from SAS — not an advertisement and not marketing content, just a clear note about the tools used to build this platform.",
  },
};

export default function BuiltWithEmergent() {
  const { lang } = useLanguage();
  const c = C[lang];

  return (
    <>
      <section className="sas-section-sm border-b border-[color:var(--sas-line)]" data-testid="bwe-header">
        <div className="sas-container">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="sas-hero-mark" aria-hidden />
              <span className="sas-eyebrow">{c.eyebrow}</span>
            </div>
            <h1 className="sas-h1">
              {c.title}‎ <span dir="ltr" className="inline-block">Emergent</span>
            </h1>
            <p className="sas-lead mt-5">{c.intro}</p>
          </div>
        </div>
      </section>

      <section className="sas-section" data-testid="bwe-why-used">
        <div className="sas-container grid lg:grid-cols-[1fr_1.5fr] gap-10">
          <div>
            <div className="sas-eyebrow mb-3">01</div>
            <h2 className="sas-h2">
              {c.h1}‎ <span dir="ltr" className="inline-block">Emergent</span>
            </h2>
          </div>
          <div>
            <p className="sas-body text-[16.5px] leading-[2]">{c.p1}</p>
          </div>
        </div>
      </section>

      <div className="sas-divider" />

      <section className="sas-section" data-testid="bwe-why-recommend">
        <div className="sas-container grid lg:grid-cols-[1fr_1.5fr] gap-10">
          <div>
            <div className="sas-eyebrow mb-3">02</div>
            <h2 className="sas-h2">{c.h2}</h2>
          </div>
          <div>
            <p className="sas-body text-[16.5px] leading-[2]">{c.p2}</p>
          </div>
        </div>
      </section>

      <div className="sas-divider" />

      <section className="sas-section" data-testid="bwe-closing">
        <div className="sas-container">
          <div className="max-w-3xl">
            <p className="sas-h3" style={{ lineHeight: "1.9", fontWeight: 500 }}>{c.closing}</p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <a href={EMERGENT_URL} target="_blank" rel="noreferrer noopener" className="sas-btn sas-btn-primary" data-testid="bwe-cta">
                {c.visit} <span dir="ltr">Emergent</span>
                <ArrowUpLeft size={16} />
              </a>
              <LocalizedLink to="/about" className="sas-btn sas-btn-ghost">{c.back}</LocalizedLink>
            </div>
            <div className="mt-10 pt-6 border-t border-[color:var(--sas-line)]">
              <p className="text-xs text-[color:var(--sas-ink-2)]">{c.note}</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
